import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { ExternalLink } from "ExternalLink";
import { useAchievements } from "api/achievements";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useEvents } from "api/events";
import { useAppSelector } from "app/hooks";
import { StepperFormButtons } from "components/FormControls/StepperFormButtons";
import {
  getTransactionAttribute,
  getTransactionParam,
  sortCampaignTranslations,
} from "components/Layout/Campaign/Common/campaignHelpers";
import { Stepper } from "components/Stepper/Stepper";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { OLModal } from "components/Tooltips/OLModal";
import { surveyQuery } from "constants/general";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik, FormikErrors } from "formik";
import { useMessage } from "hooks/intl";
import { useStepper } from "hooks/useStepper";
import { get, isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  selectAvaiableTranslations,
  selectDefaultLanguageData,
} from "reduxState/translations/translationsSlice";
import {
  AchievementAggregation,
  AchievementBEData,
  AchievementBEFormat,
  AchievementBEFormatWithoutRuleId,
  AchievementFEFormat,
  AchievementRuleTypes,
  AchievementTriggers,
  CompleteRule,
} from "types/achievements";
import { CampaignConditionType } from "types/campaigns";
import {
  AchievementStepsIds,
  AchievementStepsUnion,
  Status,
  StepDetails,
} from "types/stepper";
import {
  backendErrors,
  parseCampaignTranslationsArrayError,
} from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { InfoBox } from "../../InfoBox";
import { getCurrentName } from "../AchievementImportList/utils";
import {
  achievementFormSteps,
  checkIfFieldsThatResetProgressWereModified,
  customEventReferralAchievementConditions,
  formatPatchSumbitValues,
  formatSubmitValues,
  getFieldsForFeValidation,
  getStepIdToSetError,
  getValidationSchemaByStep,
  parseRulesErrors,
  parseToFEPeriod,
  prepareBEAchievementLimitFormatForFE,
  transactionReferralAchievementConditions,
} from "../helpers";
import { useStyles } from "./AchievementForm.styles";
import { AchievementInfo } from "./AchievementInfo/AchievementInfo";
import { Rules } from "./Rules/Rules";
import { Summary } from "./Summary/Summary";

export const defaultRule = {
  aggregation: { type: AchievementAggregation.QUANTITY },
  completeRule: {},
  conditions: [],
  uniqueReferee: undefined,
  trigger: AchievementTriggers.TRANSACTION,
  type: AchievementRuleTypes.DIRECT,
};

export function AchievementForm(props: {
  isEditMode?: boolean;
  initialValues?: AchievementBEFormat;
  setAchievementName?: (name: string | undefined) => void;
  initialErrors?: FormikErrors<AchievementFEFormat>;
  onSaveCallback?: (achievement: AchievementBEData & { name: string }) => void;
  onCancelCallback?: () => void;
}) {
  const { message } = useMessage();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { notify } = useNotifications();
  const classes = useStyles();
  const { id: achievementId } = useParams<{ id: string }>();
  const { addAchievement, editAchievement, patchAchievement } =
    useAchievements();
  const availableLanguages = useAppSelector(selectAvaiableTranslations);
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const eventCampaignAttribute = /event.body.(.*)/;
  const { useCustomEventSchemaDetails } = useEvents();

  const NthSchemaDetails = (index: number) => {
    const { data: selectedSchemaDetails } = useCustomEventSchemaDetails(
      get(props, `initialValues.rules[${index}].event`),
    );
    return selectedSchemaDetails;
  };

  const {
    ref,
    steps,
    isLastStep,
    activeStep,
    setSteps,
    setStepsErrors,
    skipToStep,
    prevStep,
  } = useStepper(achievementFormSteps);

  const [beErrors, setBeErrors] = useState<FormikErrors<AchievementFEFormat>>();
  const [feErrors, setFeErrors] = useState<
    | {
        [stepId: string]: FormikErrors<AchievementFEFormat>;
      }
    | undefined
  >();

  const [isConfirmationModalVisible, setConfirmationModalVisibility] =
    useState(false);
  const [isConfirmationCheckboxChecked, setConfirmationCheckboxChecked] =
    useState(false);

  const { ACHIEVEMENT_INFO, RULES, SUMMARY } = AchievementStepsIds;
  const { ACTIVE, VISITED } = Status;

  const nextStepInfo = isLastStep
    ? activeStep
    : steps[steps.findIndex((step) => step.id === activeStep.id) + 1];

  const getDefaultType = (
    attribute: string,
    ruleIndex: number,
  ): CampaignConditionType | undefined | string => {
    if (attribute?.match(eventCampaignAttribute)) {
      return (
        NthSchemaDetails(ruleIndex)?.schema?.fields.find(
          ({ name }: { name: string }) => `event.body.${name}` === attribute,
        )?.type || ""
      );
    } else {
      return (
        [
          ...transactionReferralAchievementConditions,
          ...customEventReferralAchievementConditions,
        ].find((condition) => condition.attribute === attribute)?.type || ""
      );
    }
  };

  const initialValues: AchievementFEFormat =
    props.initialValues && props.initialValues?.rules
      ? {
          active: props.initialValues.active,
          limit: prepareBEAchievementLimitFormatForFE(
            props.initialValues.limit,
          ),
          activity: isEmpty(props.initialValues.activity)
            ? { operator: "always_available" }
            : props.initialValues.activity,
          translations:
            props.initialValues.translations &&
            sortCampaignTranslations(
              defaultLang?.code,
              Object.keys(props.initialValues.translations).map((key) => ({
                description:
                  props.initialValues?.translations[key]?.description,
                locale: key,
                name: props.initialValues?.translations[key]?.name,
              })),
            ),
          rules: props.initialValues.rules.map((item, index) =>
            (({ conditions, limit, completeRule, translations, ...rest }) => {
              return {
                ...rest,
                completeRule: {
                  period: parseToFEPeriod(completeRule.period),
                  periodGoal: parseFloat(completeRule.periodGoal.toString()),
                  isEditing: false,
                  //FE only
                  onlyUniqueAttributeValues: !!completeRule.uniqueAttribute,
                  uniqueAttribute: completeRule.uniqueAttribute || undefined,
                },
                limit: prepareBEAchievementLimitFormatForFE(limit),
                conditions: conditions?.map((condition) => {
                  return {
                    attribute:
                      condition.operator === "expression"
                        ? "expression"
                        : getTransactionAttribute(condition.attribute!),
                    data:
                      getDefaultType(
                        getTransactionAttribute(condition.attribute!),
                        index,
                      ) === "boolean"
                        ? !!condition.data
                        : condition.data || "",
                    label: condition.attribute,
                    operator: condition.operator,
                    param: getTransactionParam(condition.attribute!),
                    type:
                      condition.operator === "expression"
                        ? "expression"
                        : getDefaultType(
                            getTransactionAttribute(condition.attribute!),
                            index,
                          ),
                    isSaved: true,
                    isEditing: false,
                  };
                }),
                translations: translations
                  ? sortCampaignTranslations(
                      defaultLang?.code,
                      Object.keys(translations).map((key) => ({
                        description: translations[key]?.description,
                        locale: key,
                        name: translations[key]?.name,
                      })),
                    )
                  : defaultLang && [
                      {
                        description: "",
                        locale: defaultLang.code,
                        name: "",
                      },
                    ],
              };
            })(item),
          ),
        }
      : {
          active: false,
          activity: { operator: "always_available" },
          limit: { interval: { type: "noLimit" } },
          rules: [
            {
              translations: [
                defaultLang && {
                  description: "",
                  locale: defaultLang.code,
                  name: "",
                },
              ],
              aggregation: { type: AchievementAggregation.QUANTITY },
              completeRule: {} as CompleteRule,
              conditions: [],
              uniqueReferee: undefined,
              trigger: AchievementTriggers.TRANSACTION,
              type: AchievementRuleTypes.DIRECT,
              limit: {
                interval: {
                  type: "noLimit",
                },
              },
            },
          ],
          translations: [
            defaultLang && {
              description: "",
              locale: defaultLang.code,
              name: "",
            },
          ],
        };

  const getPropertiesWithoutRuleId = (
    properties: AchievementBEFormat,
  ): AchievementBEFormatWithoutRuleId => ({
    ...properties,
    rules: properties.rules?.map(({ achievementRuleId, ...rest }) => ({
      ...rest,
    })),
  });

  const handleEditAchievement = async (
    achievementId: string,
    properties: AchievementBEFormat,
    values: AchievementFEFormat,
  ) => {
    const achievementProgressIsReset =
      checkIfFieldsThatResetProgressWereModified(values, initialValues);

    if (achievementProgressIsReset) {
      return await editAchievement(
        achievementId,
        getPropertiesWithoutRuleId(properties),
      );
    } else {
      return await patchAchievement(
        achievementId,
        formatPatchSumbitValues(properties, defaultLang?.code),
      );
    }
  };

  const validationSchema = getValidationSchemaByStep(activeStep.id, message);
  const currentStepId = steps.findIndex((step) => step.status === ACTIVE);

  const parseFeErrors = (errors?: {
    [stepId: string]: FormikErrors<AchievementFEFormat>;
  }) => {
    if (!errors) {
      return;
    }

    // Check if the error has been fixed and parse it into a validation object
    const errorsObj = Object.assign(
      {},
      ...Object.values(errors).filter((error) => !isEmpty(error)),
    );

    return errorsObj;
  };

  const changeStepWithFeValidation = async (
    values: AchievementFEFormat,
    stepsDetails: StepDetails<AchievementStepsUnion>[],
    validateForm: (
      values?: Partial<AchievementFEFormat>,
    ) => Promise<FormikErrors<AchievementFEFormat>>,
  ) => {
    const errors = await validateForm(
      getFieldsForFeValidation(activeStep.id, values),
    );

    const feErrorsState = {
      ...feErrors,
      ...{ [activeStep.id]: errors },
    };

    setFeErrors(feErrorsState);

    const parsedFeErrors = parseFeErrors(feErrorsState);

    const stepsIdsWithErrors = getStepIdToSetError(
      { ...beErrors, ...parsedFeErrors },
      values,
    );

    const updatedSteps = stepsDetails.map((step) =>
      stepsIdsWithErrors.includes(
        step.id as typeof ACHIEVEMENT_INFO | typeof RULES,
      )
        ? {
            ...step,
            hasError: true,
          }
        : { ...step, hasError: false },
    );

    setSteps(updatedSteps);
  };

  useEffect(() => {
    if (props.setAchievementName && defaultLang && initialValues) {
      const { code } = defaultLang;
      const achievementName = initialValues.translations.find(
        (translation) => translation.locale === code,
      );

      if (achievementName) {
        props.setAchievementName(achievementName.name);
      }
    }

    // eslint-disable-next-line
  }, [defaultLang, props.initialValues]);

  useEffect(() => {
    // Highlight steps as error if props.initalErrors exist, e.g. in achievement imports
    if (!isEmpty(props.initialErrors)) {
      setBeErrors(props.initialErrors);
      setStepsErrors(getStepIdToSetError(props.initialErrors, initialValues));
    }
    // eslint-disable-next-line
  }, [props.initialErrors]);

  return (
    <Formik
      initialErrors={beErrors}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async ({ name, ...values }, { setSubmitting, setErrors }) => {
        setSubmitting(true);

        try {
          const properties = formatSubmitValues(values);
          const response =
            props.isEditMode && props.initialValues
              ? await handleEditAchievement(
                  achievementId as string,
                  properties,
                  values,
                )
              : await addAchievement(getPropertiesWithoutRuleId(properties));

          props.onSaveCallback
            ? props.onSaveCallback({
                ...properties,
                name: defaultLang?.code
                  ? getCurrentName(properties.translations, defaultLang.code)
                  : (name as string),
                achievementId:
                  typeof achievementId === "string" && props.isEditMode
                    ? achievementId
                    : (response as { data: { achievementId: string } }).data
                        ?.achievementId,
              })
            : navigate(
                props.isEditMode && state?.prevPath
                  ? state?.prevPath
                  : props.isEditMode
                    ? links.achievementsList
                    : `${links.achievementsList}${surveyQuery}`,
              );
          notify(
            message({
              id: props.isEditMode ? "achievementEdited" : "achievementAdded",
            }),
          );
        } catch (exception) {
          setSubmitting(false);
          notify(getErrorMessage(exception));
          const errors = backendErrors(getBackendErrors(exception));

          const parsedErrors = {
            ...errors,
            translations: parseCampaignTranslationsArrayError(
              errors?.translations,
              values?.translations,
            ),
            rules: parseRulesErrors(errors?.rules, values.translations),
          };

          setErrors(parsedErrors);
          focusError();

          setStepsErrors(getStepIdToSetError(parsedErrors, values));
          setBeErrors(errors);
        }
      }}
    >
      {({
        dirty,
        values,
        isSubmitting,
        handleSubmit,
        validateForm,
        setFieldValue,
        setErrors,
        initialValues,
      }) => {
        const { translations, activity, limit, rules } = values;

        return (
          <>
            <div className={classes.content}>
              <Box className={classes.stepperBox}>
                <Box className={classes.stepsWrapper}>
                  <Stepper
                    steps={steps}
                    setSteps={async (
                      stepsDetails: StepDetails<AchievementStepsUnion>[],
                    ) => {
                      changeStepWithFeValidation(
                        values,
                        stepsDetails,
                        validateForm,
                      );
                    }}
                  />
                </Box>
                <div className={classes.separator} />
              </Box>
              <Form onSubmit={handleSubmit} ref={ref} className={classes.form}>
                {activeStep?.id === ACHIEVEMENT_INFO && (
                  <AchievementInfo
                    limit={limit}
                    activity={activity}
                    translations={translations}
                    availableLanguages={availableLanguages}
                    setFieldValue={setFieldValue}
                    errors={{ ...beErrors, ...parseFeErrors(feErrors) }}
                    setErrors={setErrors}
                  />
                )}
                {activeStep?.id === RULES && (
                  <Rules
                    rules={rules}
                    isEditMode={!!props.isEditMode}
                    availableLanguages={availableLanguages}
                    errors={{ ...beErrors, ...parseFeErrors(feErrors) }}
                  />
                )}
                {activeStep?.id === SUMMARY && (
                  <Summary
                    data={values}
                    skipToStep={skipToStep}
                    errors={{ ...beErrors, ...parseFeErrors(feErrors) }}
                  />
                )}
                <Box mt={12} />
                {!isSubmitting && (
                  <LeaveSiteGuard
                    warnBeforeRefresh
                    isDirty={dirty}
                    initialValues={initialValues}
                    currentValues={values}
                  />
                )}
                <OLModal
                  cancelText={message({ id: "cancel" })}
                  header={message({ id: "areYouSureYouWantToSaveChanges" })}
                  onClose={() => {
                    setConfirmationModalVisibility(false);
                    setConfirmationCheckboxChecked(false);
                  }}
                  open={isConfirmationModalVisible}
                  submitDisabled={!isConfirmationCheckboxChecked}
                  submitAction={() => {
                    setConfirmationModalVisibility(false);
                    return handleSubmit();
                  }}
                  submitText={message({ id: "saveChanges" })}
                  modalWidth={482}
                >
                  <div>
                    <InfoBox
                      type="warning"
                      text={message(
                        {
                          id: "editAchievementModalWarningBoxInfo",
                        },
                        {
                          link: (
                            <ExternalLink
                              className={classes.link}
                              path="https://help.openloyalty.io/main-features/achievements/managing-achievements"
                              data-test-id="managing-achievements-link"
                            >
                              {message({ id: "learnMore" })}
                            </ExternalLink>
                          ),
                          span: (chunks: React.ReactNode[]) => (
                            <span className={classes.bolded}>{chunks}</span>
                          ),
                        },
                      )}
                    />
                    <p className={classes.editAchievementModalInfo}>
                      {message({ id: "editAchievementModalInfo" })}
                    </p>
                    <FormControlLabel
                      label={message({
                        id: "thisActionWillResetProgress",
                      })}
                      control={
                        <Checkbox
                          color="primary"
                          checked={isConfirmationCheckboxChecked}
                          onChange={(e) =>
                            setConfirmationCheckboxChecked(
                              !isConfirmationCheckboxChecked,
                            )
                          }
                        />
                      }
                      classes={{ label: classes.checkboxLabel }}
                    />
                  </div>
                </OLModal>
              </Form>
            </div>
            <StepperFormButtons
              disableSubmit={isSubmitting}
              onCancelClick={() => {
                if (props.onCancelCallback) {
                  return props.onCancelCallback();
                }
                navigate(links.achievementsList);
              }}
              onPrevStepClick={() => {
                prevStep();
                changeStepWithFeValidation(values, steps, validateForm);
              }}
              onSubmitClick={async () => {
                if (!isLastStep) {
                  const nextStep = steps[currentStepId + 1];

                  const stepsDetails: StepDetails<AchievementStepsUnion>[] =
                    steps.map((step) => {
                      if (step.id === nextStep.id) {
                        return {
                          ...step,
                          status: ACTIVE,
                        };
                      }
                      return {
                        ...step,
                        status: step.status === ACTIVE ? VISITED : step.status,
                      };
                    });

                  changeStepWithFeValidation(
                    values,
                    stepsDetails,
                    validateForm,
                  );
                } else {
                  props.isEditMode &&
                  checkIfFieldsThatResetProgressWereModified(
                    values,
                    initialValues,
                  )
                    ? setConfirmationModalVisibility(true)
                    : handleSubmit();
                }
              }}
              submitMessage={
                !isLastStep
                  ? `${message({ id: "nextStep" })}: ${message({ id: nextStepInfo?.label })}`
                  : props.isEditMode
                    ? "saveChanges"
                    : "addAchievement"
              }
              data-test-id={!isLastStep ? "continue-button" : undefined}
            />
          </>
        );
      }}
    </Formik>
  );
}
